import React, { useEffect, useState } from "react";

/**
 * JerichoBlueprintPanel — upper-right black-site compound blueprint.
 * Inline SVG perimeter / buildings / patrol markers. Purely cosmetic.
 */
export const JerichoBlueprintPanel = () => {
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPulse((p) => (p + 1) % 360), 1500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="jericho-blueprint-panel"
      className="border border-[#2A2722] bg-[#06120F]/80 backdrop-blur-[2px] relative overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-[#2A2722] flex items-center justify-between">
        <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#5DD4B3]">
          // COMPOUND · OVERVIEW
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
          BP-04
        </div>
      </div>

      <div className="relative">
        {/* Blueprint grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "linear-gradient(rgba(93,212,179,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(93,212,179,0.18) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />
        {/* Sub-grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(rgba(93,212,179,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(93,212,179,0.10) 1px, transparent 1px)",
            backgroundSize: "5px 5px",
          }}
        />

        <svg
          viewBox="0 0 400 240"
          className="relative w-full h-[180px] sm:h-[210px]"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer perimeter */}
          <rect
            x="12" y="14" width="376" height="212"
            fill="none"
            stroke="#5DD4B3"
            strokeOpacity="0.55"
            strokeWidth="1"
            strokeDasharray="6 4"
          />
          {/* Inner restricted zone */}
          <rect
            x="40" y="40" width="320" height="160"
            fill="rgba(93,212,179,0.04)"
            stroke="#5DD4B3"
            strokeOpacity="0.4"
            strokeWidth="0.8"
          />

          {/* Buildings */}
          <g stroke="#5DD4B3" strokeOpacity="0.7" strokeWidth="0.9" fill="rgba(93,212,179,0.06)">
            <rect x="60" y="60" width="80" height="50" />
            <rect x="160" y="60" width="60" height="40" />
            <rect x="240" y="70" width="100" height="60" />
            <rect x="60" y="140" width="50" height="44" />
            <rect x="130" y="130" width="90" height="54" />
            <rect x="240" y="150" width="80" height="34" />
          </g>

          {/* Labels */}
          <g fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fontSize="6.5" fill="#5DD4B3" fillOpacity="0.85">
            <text x="64" y="74">BLOCK-A · INTAKE</text>
            <text x="164" y="74">RDX</text>
            <text x="244" y="84">VAULT · OMEGA</text>
            <text x="64" y="154">SVR</text>
            <text x="134" y="144">EVIDENCE · LAB</text>
            <text x="244" y="164">SURV-OPS</text>
          </g>

          {/* Crosshair on Vault Omega */}
          <g stroke="#D92D20" strokeWidth="0.9" strokeOpacity="0.9">
            <circle cx="290" cy="100" r="11" fill="none" />
            <circle cx="290" cy="100" r="3" fill="rgba(217,45,32,0.6)" />
            <line x1="278" y1="100" x2="285" y2="100" />
            <line x1="295" y1="100" x2="302" y2="100" />
            <line x1="290" y1="88" x2="290" y2="95" />
            <line x1="290" y1="105" x2="290" y2="112" />
          </g>

          {/* Patrol path */}
          <path
            d="M 50 50 H 350 V 190 H 50 Z"
            fill="none"
            stroke="#E0A526"
            strokeOpacity="0.45"
            strokeWidth="0.6"
            strokeDasharray="2 3"
          />

          {/* Roaming patrol marker */}
          <g transform={`rotate(${pulse} 200 120)`}>
            <circle cx="200" cy="50" r="2.5" fill="#E0A526" />
          </g>

          {/* Corner ticks */}
          <g stroke="#5DD4B3" strokeOpacity="0.7" strokeWidth="1">
            <line x1="12" y1="14" x2="22" y2="14" />
            <line x1="12" y1="14" x2="12" y2="24" />
            <line x1="388" y1="14" x2="378" y2="14" />
            <line x1="388" y1="14" x2="388" y2="24" />
            <line x1="12" y1="226" x2="22" y2="226" />
            <line x1="12" y1="226" x2="12" y2="216" />
            <line x1="388" y1="226" x2="378" y2="226" />
            <line x1="388" y1="226" x2="388" y2="216" />
          </g>
        </svg>

        {/* Compass / scale */}
        <div className="absolute bottom-2 right-2 font-mono text-[8px] uppercase tracking-[0.3em] text-[#5DD4B3]/80 bg-black/40 px-1.5 py-0.5 border border-[#5DD4B3]/30">
          N ↑ · SCALE 1:2500
        </div>
        <div className="absolute top-2 left-2 font-mono text-[8px] uppercase tracking-[0.3em] text-[#D92D20] bg-black/40 px-1.5 py-0.5 border border-[#D92D20]/40">
          LIVE · 24/7
        </div>
      </div>

      {/* Footer metrics */}
      <div className="grid grid-cols-3 border-t border-[#2A2722] divide-x divide-[#2A2722]">
        <Cell label="ZONES" value="06" tone="default" />
        <Cell label="PATROLS" value="04" tone="warn" />
        <Cell label="BREACH" value="00" tone="ok" />
      </div>
    </section>
  );
};

const Cell = ({ label, value, tone }) => {
  const txt = {
    default: "text-[#E4DFD3]",
    ok: "text-[#5DD4B3]",
    warn: "text-[#E0A526]",
    alert: "text-[#D92D20]",
  }[tone];
  return (
    <div className="px-2 py-1.5">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#7a7062]">
        {label}
      </div>
      <div className={`font-mono text-[13px] tracking-[0.2em] ${txt}`}>{value}</div>
    </div>
  );
};

export default JerichoBlueprintPanel;
